import React, { useState } from "react";
import { Flex, Avatar, Text, TextArea, AcceptIcon, CloseIcon, EditIcon, TrashCanIcon } from "@fluentui/react-northstar";
import { format } from "date-fns";
import { constants } from "shared/constants";
import { useStore } from "stores/Stores";
import { Comment as CommentModel } from "../../shared/models/Comment";
import { pl as plLocale } from "date-fns/locale";
import styles from "./comment.module.scss";
import classNames from "classnames";
import pl from "shared/messages/pl";
import { ResponsiveDialog } from "ecom-teams-apps-core";

interface CommentProps {
    comment: CommentModel;
    isMobile?: boolean;
}

const Comment: React.FC<CommentProps> = ({ comment, isMobile }) => {
    const [isBusy, setIsBusy] = useState(false);
    const [isConfirmDialogVisible, setIsConfirmDialogVisible] = useState(false);
    const [isEditable, setIsEditable] = useState(false);
    const [commentChange, setCommentChange] = useState(comment.content);

    const { tasksStore, authorizationStore } = useStore();

    const name = comment.author.fullName || comment.author.userName;
    const isMyComment = authorizationStore.currentUser?.userName === comment.author.userName;

    return (
        <Flex
            gap="gap.medium"
            className={classNames(styles.wrapper, {
                [styles.edition]: isEditable || isConfirmDialogVisible,
                [styles.myComment]: isMyComment,
            })}
            onDoubleClick={() => isMyComment && setIsEditable(true)}
        >
            <Avatar name={name} styles={{ backgroundColor: "transparent" }} />
            <Flex column fill>
                <Flex className={styles.header} gap="gap.small">
                    <Flex wrap column gap="gap.smaller">
                        <Text weight="bold" size="small" content={name} />
                        <Text
                            size="small"
                            content={format(comment.commentDate, constants.commentDateFormat, { locale: plLocale })}
                        />
                    </Flex>

                    {isMyComment && !isEditable && (
                        <Flex>
                            <EditIcon
                                className={styles.actionIcon}
                                title={pl.taskDetails.comment.edit.editAction}
                                onClick={() => setIsEditable(true)}
                            />
                            <TrashCanIcon
                                className={styles.actionIcon}
                                title={pl.taskDetails.comment.edit.deleteAction}
                                onClick={() => setIsConfirmDialogVisible(true)}
                            />
                        </Flex>
                    )}
                </Flex>
                {isEditable ? (
                    <>
                        <TextArea
                            value={commentChange}
                            fluid
                            resize="vertical"
                            variables={{ height: "120px", borderColorFocus: "none" }}
                            className={styles.textArea}
                            inverted
                            onChange={(event) =>
                                setCommentChange((event.currentTarget as EventTarget & HTMLTextAreaElement).value)
                            }
                        />
                        <Flex hAlign="end" gap="gap.small" className={styles.editionActionWrapper}>
                            <CloseIcon
                                onClick={() => {
                                    setIsEditable(false);
                                    setCommentChange(comment.content);
                                }}
                                title={pl.taskDetails.comment.edit.cancelAction}
                                className={styles.editionActionIcon}
                            />
                            <AcceptIcon
                                onClick={async () => {
                                    if (commentChange.length) {
                                        setIsEditable(false);

                                        if (
                                            commentChange !== comment.content ||
                                            (tasksStore.updateCommentRequestStatus.error &&
                                                tasksStore.lastUpdatedCommentId === comment.id)
                                        ) {
                                            setIsBusy(true);
                                            await tasksStore.updateComment(
                                                tasksStore.selectedTask!.id,
                                                comment.id,
                                                commentChange
                                            );

                                            if (tasksStore.updateCommentRequestStatus.error) {
                                                setIsEditable(true);
                                            }

                                            setIsBusy(false);
                                        }
                                    }
                                }}
                                title={pl.taskDetails.comment.edit.saveAction}
                                className={styles.editionActionIcon}
                                disabled={!commentChange.length}
                            />
                        </Flex>
                    </>
                ) : (
                    <Text
                        size={isBusy ? "small" : "medium"}
                        content={isBusy ? pl.taskDetails.comment.edit.loader : commentChange}
                        disabled={isBusy}
                        className={isBusy ? styles.loader : styles.commentText}
                    />
                )}
                <ResponsiveDialog
                    cancelButton={pl.common.close}
                    confirmButton={pl.common.confirm}
                    onConfirm={async () => {
                        setIsConfirmDialogVisible(false);
                        setIsBusy(true);
                        await tasksStore.deleteComment(tasksStore.selectedTask!.id, comment.id);
                        setIsBusy(false);
                    }}
                    onCancel={() => setIsConfirmDialogVisible(false)}
                    header={pl.taskDetails.comment.delete.header}
                    content={pl.taskDetails.comment.delete.message}
                    open={isConfirmDialogVisible}
                    isMobile={isMobile}
                    gap={"gap.small"}
                />
            </Flex>
        </Flex>
    );
};

export default Comment;
